import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { useLoader } from '../api/useLoader';
import {
  BrukerrollerPSDto,
  BrukerrollerPSDto as UHBrukerrollerDto,
  HttpError,
  MuligePraksisstedDto as UHMuligPraksisstedDto,
} from '../api/generated/studiested-generated-api';
import {
  BrukerrollerDto as PSBrukerrollerDto,
  MuligPraksisstedDto as PSMuligPraksisstedDto,
} from '../api/generated/praksissted-generated-api';
import LocalAlert, {
  AlertType,
  LocalAlertProps,
  PropsHidden,
} from '../components/notifications/localAlert';
import { useIntl } from 'react-intl';
import translations from './invitePsa.i18n';
import { ToolbarLink } from '../pagelayout/toolbarLink';
import { FormControlLabel, FormGroup, Grid } from '@mui/material';
import PageLayout from '../pagelayout';
import PanelHeader from '../components/panelheader/panelHeader';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '../components/input/autocomplete';
import Button, { ButtonVariant } from '../components/button';
import Spinner from '../components/spinner/spinner';
import { TextInput } from '../components/input/textInput';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import { IndentMuligPraksissted } from '../components/indentation/indentation';
import Dropdown from '../components/input/dropdown';
import validateEmail from '../util/validateEmail';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { StedType } from '../components/hierarchy/onhoverhierarchyinfo';

type Brukerroller = UHBrukerrollerDto | PSBrukerrollerDto;
type MuligPraksissted = UHMuligPraksisstedDto | PSMuligPraksisstedDto;

export const InvitePsUserPagePSA: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { praksisstedApi } = useContext(PraksisstedApiContext);

  const { data } = useLoader(
    () => praksisstedApi.administrertePraksistederGet(),
    [],
  );

  const postNewPsUserInvitePSA = (
    fornavn: string,
    etternavn: string,
    epost: string,
    useFnr: boolean,
    fnr: string,
    praksisstedId: string,
    brukerrolle: string,
  ) =>
    praksisstedApi.inviterPraksisstedbrukerPost({
      nyPraksisstedbrukerInvitasjonDto: {
        fornavn,
        etternavn,
        epost,
        praksisstedId,
        fnr: useFnr ? fnr : undefined,
        tilgangsrolle: brukerrolle as PSBrukerrollerDto,
      },
    });

  return (
    <InvitePsUserPage
      data={data}
      postNewPsUserInvite={postNewPsUserInvitePSA}
    />
  );
};

export const InvitePsUserPageUHA = () => {
  const { praksisstedApi } = useContext(StudiestedApiContext);
  const { placeId } = useParams<{ placeId: string }>();

  const { data } = useLoader(() => {
    if (!placeId) return Promise.reject('No practice place id');
    return praksisstedApi.inviterPraksisstedbrukerGet({
      queryParams: {
        id: placeId,
      },
    });
  }, [placeId]);

  const postNewPsUserInviteUHA = (
    fornavn: string,
    etternavn: string,
    epost: string,
    useFnr: boolean,
    fnr: string,
    praksisstedId: string,
    brukerrolle: string,
  ) =>
    praksisstedApi.inviterPraksisstedbrukerPost({
      nyPraksisstedbrukerInvitasjonDto: {
        fornavn,
        etternavn,
        epost,
        praksisstedId,
        fnr: useFnr ? fnr : undefined,
        brukerrolle: brukerrolle as UHBrukerrollerDto,
      },
    });

  const defaultPraksissted: UHMuligPraksisstedDto | undefined = useMemo(
    () => data?.find((element) => element.id === placeId),
    [data, placeId],
  );

  return (
    <InvitePsUserPage
      data={data}
      postNewPsUserInvite={postNewPsUserInviteUHA}
      defaultPraksissted={defaultPraksissted}
      isUHAUser
    />
  );
};

interface InvitePsUserPageProps {
  data: MuligPraksissted[] | undefined;
  postNewPsUserInvite: (
    fornavn: string,
    etternavn: string,
    epost: string,
    useFnr: boolean,
    fnr: string,
    praksisstedId: string,
    brukerrolle:
      | keyof typeof UHBrukerrollerDto
      | keyof typeof PSBrukerrollerDto,
  ) => Promise<void>;
  isUHAUser?: boolean;
  defaultPraksissted?: UHMuligPraksisstedDto;
}

type ILocalAlert = LocalAlertProps | PropsHidden;

const InvitePsUserPage: React.FC<
  React.PropsWithChildren<InvitePsUserPageProps>
> = ({ data, postNewPsUserInvite, isUHAUser, defaultPraksissted }) => {
  const { addNotification } = useContext(NotificationFunctionContext);
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const [fornavn, setFornavn] = useState('');
  const [etternavn, setEtternavn] = useState('');
  const [epost, setEpost] = useState('');
  const [useFnr, setUseFnr] = useState(false);
  const [fnr, setFnr] = useState('');
  const [brukerrolle, setBrukerrolle] = useState<Brukerroller>(
    isUHAUser
      ? UHBrukerrollerDto.Administrator
      : PSBrukerrollerDto.Skriverettighet,
  );
  const [selectedPraksissted, setSelectedPraksissted] =
    useState<MuligPraksissted>();

  const [mayShowEmailInfo, setMayShowEmailInfo] = useState(false);
  const [emailInputFocusTimer, setEmailInputFocusTimer] =
    useState<NodeJS.Timeout>();
  const [showAlert, setShowAlert] = useState<ILocalAlert>({ open: false });

  // Based on translations having ids on the form {title}Uha or {title}Psa
  const keyMessage = useCallback(
    (title: string) => (isUHAUser ? `${title}Uha` : `${title}Psa`),
    [isUHAUser],
  );

  const labels = {
    title: formatMessage({
      id: translations[keyMessage('title')].id,
    }),
    subtitle: formatMessage({
      id: translations[keyMessage('subtitle')].id,
    }),
    save: formatMessage({ id: translations.save.id }),
    cancel: formatMessage({ id: translations.cancel.id }),
    usertitle: formatMessage({ id: translations.usertitle.id }),
    usersubtitle: formatMessage({
      id: translations[keyMessage('usersubtitle')].id,
    }),
    usergivenname: formatMessage({ id: translations.usergivenname.id }),
    usersurname: formatMessage({ id: translations.usersurname.id }),
    useremail: formatMessage({ id: translations.useremail.id }),
    invitewithfnr: formatMessage({ id: translations.invitewithfnr.id }),
    userfnr: formatMessage({ id: translations.userfnr.id }),
    accesstitle: formatMessage({ id: translations.accesstitle.id }),
    accesssubtitle: formatMessage({ id: translations.accesssubtitle.id }),
    accessplace: formatMessage({ id: translations.accessplace.id }),
    accessplaceholder: formatMessage({ id: translations.accessplaceholder.id }),
    roleChoose: formatMessage({ id: translations.roleChoose.id }),
    rolePermission: formatMessage({ id: translations.rolePermission.id }),
    invalidEmail: formatMessage({ id: translations.invalidEmail.id }),
  };

  const translateRolle = (rolle: Brukerroller) =>
    formatMessage({ id: translations[`rolle${rolle}`].id });

  const navigateToUrl = useMemo(
    () => (isUHAUser ? '/practiceplaces' : '/myorganization'),
    [isUHAUser],
  );

  const validEmail = useMemo(() => validateEmail(epost), [epost]);

  const submitInvite = useCallback(() => {
    postNewPsUserInvite(
      fornavn,
      etternavn,
      epost,
      useFnr,
      fnr,
      selectedPraksissted?.id || '',
      brukerrolle,
    )
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: formatMessage(
            { id: translations.success.id },
            { email: epost },
          ),
        });
        navigate(navigateToUrl);
      })
      .catch((err: HttpError) => {
        setShowAlert({
          open: true,
          contents: err.body,
          type: AlertType.ERROR,
          handleClose: () => setShowAlert({ open: false }),
        });
      });
  }, [
    fornavn,
    etternavn,
    epost,
    useFnr,
    fnr,
    selectedPraksissted,
    brukerrolle,
    formatMessage,
    addNotification,
    navigate,
    navigateToUrl,
    postNewPsUserInvite,
  ]);

  const cancelButton = useMemo(
    () =>
      ToolbarLink({
        title: labels.cancel,
        exact: true,
        path: navigateToUrl,
        icon: undefined,
        buttonVariant: ButtonVariant.Tertiary,
      }),
    [labels.cancel, navigateToUrl],
  );

  // TODO: validate fnr?
  const saveDisabled = useMemo(
    () =>
      !(
        fornavn &&
        etternavn &&
        epost &&
        validEmail &&
        selectedPraksissted &&
        (!useFnr || (fnr && fnr.length === 11))
      ),
    [fornavn, etternavn, epost, validEmail, selectedPraksissted, useFnr, fnr],
  );

  const actionItems = useMemo(
    () => (
      <>
        {cancelButton}
        <Button.Primary onClick={submitInvite} disabled={saveDisabled}>
          {labels.save}
        </Button.Primary>
      </>
    ),
    [cancelButton, labels.save, saveDisabled, submitInvite],
  );

  useEffect(() => {
    if (defaultPraksissted) {
      setSelectedPraksissted(defaultPraksissted);
    }
  }, [defaultPraksissted]);

  const resetEmailInputTimer = () => {
    if (emailInputFocusTimer) clearTimeout(emailInputFocusTimer);
    setMayShowEmailInfo(false);
  };

  const onEmailFieldChange = (value: string) => {
    resetEmailInputTimer();
    setEpost(value);

    if (value) {
      setEmailInputFocusTimer(
        setTimeout(() => {
          setMayShowEmailInfo(true);
        }, 1250), // Amount of time (ms), before invalid email warning may be shown
      );
    }
  };

  const onEmailFieldBlur = () => {
    if (emailInputFocusTimer) clearTimeout(emailInputFocusTimer);
    if (epost.length > 0) setMayShowEmailInfo(true);
  };

  return (
    <PageLayout
      title={labels.title}
      subtitle={labels.subtitle}
      actionItems={actionItems}
      isIndented
    >
      {data ? (
        <>
          <LocalAlert {...showAlert} />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <PanelHeader
                title={labels.usertitle}
                subtitle={labels.usersubtitle}
              />
            </Grid>
            <Grid item xs={4}>
              <TextInput
                htmlId={'fornavn'}
                label={labels.usergivenname}
                value={fornavn}
                onChange={setFornavn}
                autoComplete={'off'}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <TextInput
                htmlId={'etternavn'}
                label={labels.usersurname}
                value={etternavn}
                onChange={setEtternavn}
                autoComplete={'off'}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <TextInput
                htmlId={'epost'}
                label={labels.useremail}
                value={epost}
                onChange={onEmailFieldChange}
                autoComplete={'off'}
                onFocus={resetEmailInputTimer}
                onBlur={onEmailFieldBlur}
                helpText={
                  !validEmail && mayShowEmailInfo && <>{labels.invalidEmail}</>
                }
                required
              />
            </Grid>
            <Grid container item xs={12} spacing={1} direction={'column'}>
              <Grid item xs={4}>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e, c) => {
                          setUseFnr(c);
                        }}
                        checked={useFnr}
                      />
                    }
                    label={labels.invitewithfnr}
                  />
                </FormGroup>
              </Grid>
              {useFnr && (
                <Grid item xs={4}>
                  <TextInput
                    htmlId={'fnr'}
                    label={labels.userfnr}
                    value={fnr}
                    onChange={setFnr}
                    autoComplete={'off'}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <PanelHeader
                title={labels.accesstitle}
                subtitle={labels.accesssubtitle}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                id={'autocomplete'}
                options={data.map((praksissted) => ({
                  ...praksissted,
                  onHoverType: StedType.PRAKSISSTED,
                }))}
                defaultValue={defaultPraksissted}
                onChange={(event: object, value: MuligPraksissted) =>
                  setSelectedPraksissted(value)
                }
                label={labels.accessplace}
                placeholder={labels.accessplaceholder}
                renderOption={(option) => (
                  <IndentMuligPraksissted praksissted={option} />
                )}
                required
              />
            </Grid>

            <Grid item xs={4}>
              <Dropdown
                id={'role-select'}
                onChange={(event, value) =>
                  setBrukerrolle(value.id as Brukerroller)
                }
                options={Object.keys(BrukerrollerPSDto).map((rolle) => ({
                  id: rolle,
                  navn: translateRolle(rolle as Brukerroller),
                }))}
                label={labels.rolePermission}
                defaultValue={{
                  id: brukerrolle,
                  navn: translateRolle(brukerrolle),
                }}
                placeholder={labels.roleChoose}
                required
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <Spinner />
      )}
    </PageLayout>
  );
};
